import { useCallback } from "react";

import {
  applyGameAction as applyGameActionCallable,
  newActionId,
  timed,
} from "../data/callables";
import * as intents from "../data/intents";
import type {
  ActiveGameViewModel,
  BaseIndex,
  GameAction,
  GameModel,
  GameResultKey,
} from "../shared/types/contracts";
import { responseRevision } from "./commandTypes";
import type { EnqueueCommand, RunCommand } from "./commandTypes";
import type { OptimisticGameController } from "./useOptimisticGame";
import type { PlayDraftController } from "./usePlayDraft";
import type { TeamCommands } from "./teamTypes";
import { actionKeys } from "./actionKeys";
import { eventRecovery } from "./useSerializedGameQueue";

export type GameActionCommand = <Type extends GameAction["type"]>(
  type: Type,
  payload: Extract<GameAction, { type: Type }>["payload"],
  statusKey?: string,
  onCommitted?: (revision: number) => void,
  onAcknowledged?: () => void,
) => Promise<boolean>;

interface GameCommandOptions {
  teamId: string;
  game: ActiveGameViewModel | null;
  rawGame: GameModel | null | undefined;
  playDraft: PlayDraftController;
  run: RunCommand;
  enqueue: EnqueueCommand;
  project: OptimisticGameController["project"];
  projectUndo: OptimisticGameController["projectUndo"];
  markCommitted: OptimisticGameController["markCommitted"];
  rejectProjection: OptimisticGameController["reject"];
  showToast: (message: string) => void;
  scoringBlocked: boolean;
  scorerName: string | null;
  onScorerClaimed: (name: string | null) => void;
}

export type GameCommandController = Pick<
  TeamCommands,
  | "recordPlay"
  | "applyRBI"
  | "advanceRunner"
  | "removeRunner"
  | "addOppRun"
  | "endHalf"
  | "undo"
  | "toggleOpenInning"
  | "keepPlaying"
  | "endAndArchive"
  | "forfeitGame"
  | "discardActiveGame"
  | "claimScoring"
> & {
  gameAction: GameActionCommand;
};

export function useGameCommands({
  teamId,
  game,
  rawGame,
  playDraft,
  run,
  enqueue,
  project,
  projectUndo,
  markCommitted,
  rejectProjection,
  showToast,
  scoringBlocked,
  scorerName,
  onScorerClaimed,
}: GameCommandOptions): GameCommandController {
  const blockedByScorer = (): boolean => {
    if (!scoringBlocked) return false;
    showToast(
      scorerName
        ? `${scorerName} is scoring — take over to make changes`
        : "Claim scoring before making changes",
    );
    return true;
  };

  const gameAction = useCallback<GameActionCommand>(
    (type, payload, statusKey, onCommitted, onAcknowledged) => {
      if (!game || !rawGame) return Promise.resolve(false);
      if (scoringBlocked) {
        showToast(
          scorerName
            ? `${scorerName} is scoring — take over to make changes`
            : "Claim scoring before making changes",
        );
        return Promise.resolve(false);
      }
      const actionId = newActionId();
      const projection = project({
        actionId,
        type,
        payload,
      } as Parameters<OptimisticGameController["project"]>[0]);
      const expectedRevision = projection.expectedRevision;
      return enqueue(
        () =>
          run(
            () =>
              timed(
                `game:${type}`,
                applyGameActionCallable({
                  teamId,
                  gameId: game.id,
                  actionId,
                  expectedRevision,
                  action: { type, payload } as GameAction,
                }),
              ),
            (response) => {
              const committedRevision = responseRevision(response);
              if (committedRevision !== null) {
                markCommitted(actionId, committedRevision);
                onCommitted?.(committedRevision);
              }
            },
          ),
        {
          key: statusKey ?? actionKeys.gameAction(game.id, type),
          chainKey: `game:${game.id}`,
          intentId: actionId,
          onChainFailure: () => rejectProjection(actionId),
          recovery: eventRecovery(teamId, actionId, (revision) => {
            markCommitted(actionId, revision);
            onCommitted?.(revision);
          }),
          ...(projection.acknowledgement
            ? { acknowledgement: projection.acknowledgement }
            : {}),
          finishStatusOnCommit: true,
          ...(onAcknowledged ? { onAcknowledged } : {}),
        },
      );
    },
    [
      teamId,
      game,
      rawGame,
      project,
      markCommitted,
      rejectProjection,
      enqueue,
      run,
      scoringBlocked,
      scorerName,
      showToast,
    ],
  );

  const recordPlay: TeamCommands["recordPlay"] = (result, decisions, rbi) => {
    if (!game || !game.lineup.length) return Promise.resolve(false);
    const batter = game.lineup[game.batterIdx % game.lineup.length];
    if (!batter?.id) return Promise.resolve(false);
    return gameAction("recordPlateAppearance", {
      batterId: batter.id,
      resultKey: result.key as GameResultKey,
      runnerDecisions: decisions,
      rbi: rbi as 0 | 1 | 2 | 3 | 4,
    });
  };
  const applyRBI = async (runs: number): Promise<boolean> => {
    const pendingRbi = playDraft.pendingRBI;
    if (!pendingRbi) return false;
    if (runs === 0) {
      playDraft.resolvePendingRbi(pendingRbi);
      return true;
    }
    if (!pendingRbi.batterId || !game) return false;
    const succeeded = await gameAction(
      "attributeRbi",
      {
        batterId: pendingRbi.batterId,
        n: runs as 1 | 2 | 3 | 4,
      },
      actionKeys.rbi(game.id),
      undefined,
      () => playDraft.resolvePendingRbi(pendingRbi),
    );
    return succeeded;
  };
  const advanceRunner = async (baseIndex: number): Promise<void> => {
    if (!game) return;
    const lastBatter = game.lastBatterId;
    const succeeded = await gameAction(
      "adjustRunner",
      {
        baseIndex: baseIndex as BaseIndex,
        destination: (baseIndex === 2 ? "H" : baseIndex + 1) as BaseIndex | "H",
      },
      actionKeys.runnerAdvance(game.id, baseIndex),
    );
    if (succeeded && baseIndex === 2) {
      playDraft.setPendingRBI({ batterId: lastBatter, runs: 1 });
    }
  };
  const removeRunner = (baseIndex: number): Promise<boolean> =>
    gameAction(
      "removeRunner",
      { baseIndex: baseIndex as BaseIndex },
      game ? actionKeys.runnerRemove(game.id, baseIndex) : undefined,
    );
  const addOppRun = (delta: number): Promise<boolean> =>
    gameAction(
      "addOpponentRun",
      { delta: delta as 1 | -1 },
      game ? actionKeys.opponentRun(game.id) : undefined,
    );
  const endHalf = (): Promise<boolean> =>
    gameAction(
      "endHalfInning",
      {},
      game ? actionKeys.endHalf(game.id) : undefined,
    );
  const toggleOpenInning = (): Promise<boolean> =>
    gameAction(
      "updateLiveSetup",
      {
        openInning: !game?.openInning,
      },
      game ? actionKeys.openInning(game.id) : undefined,
    );
  const keepPlaying = (): Promise<boolean> =>
    gameAction(
      "updateLiveSetup",
      { final: false },
      game ? actionKeys.keepPlaying(game.id) : undefined,
    );
  const undo = (): Promise<boolean> => {
    if (!game || blockedByScorer()) return Promise.resolve(false);
    const actionId = newActionId();
    const projection = projectUndo(actionId);
    if (!projection) {
      showToast("The last play is still syncing — try undo again");
      return Promise.resolve(false);
    }
    return enqueue(
      () =>
        run(
          () =>
            intents.sendUndo({
              teamId,
              gameId: game.id,
              expectedRevision: projection.expectedRevision,
              actionId,
            }),
          (response) => {
            const committedRevision = responseRevision(response);
            if (committedRevision !== null) {
              markCommitted(actionId, committedRevision);
            }
          },
        ),
      {
        key: actionKeys.undo(game.id),
        chainKey: `game:${game.id}`,
        intentId: actionId,
        onChainFailure: () => rejectProjection(actionId),
        recovery: eventRecovery(teamId, actionId, (revision) =>
          markCommitted(actionId, revision),
        ),
        acknowledgement: projection.acknowledgement,
        finishStatusOnCommit: true,
      },
    );
  };
  const endAndArchive = async (): Promise<boolean> => {
    if (!game || blockedByScorer()) return false;
    let revision = game.revision;
    const finalizeActionId = newActionId();
    const archiveActionId = newActionId();
    if (game.status !== "final") {
      const response = await enqueue(
        async () => {
          let committedRevision: number | null = null;
          const succeeded = await run(
            () =>
              intents.sendFinalize({
                teamId,
                gameId: game.id,
                expectedRevision: revision,
                actionId: finalizeActionId,
              }),
            (value) => {
              committedRevision = responseRevision(value);
            },
          );
          return succeeded ? committedRevision : null;
        },
        {
          key: actionKeys.finalize(game.id),
          chainKey: `game:${game.id}`,
          intentId: finalizeActionId,
          recovery: {
            teamId,
            actionId: finalizeActionId,
            resolve: (revision: number | null) => revision ?? undefined,
          },
        },
      );
      if (response === false || response === null) return false;
      revision = response;
    }
    const succeeded = await enqueue(
      () =>
        run(() =>
          intents.sendArchive({
            teamId,
            gameId: game.id,
            expectedRevision: revision,
            actionId: archiveActionId,
          }),
        ),
      {
        key: actionKeys.archive(game.id),
        chainKey: `game:${game.id}`,
        intentId: archiveActionId,
        recovery: eventRecovery(teamId, archiveActionId),
      },
    );
    if (succeeded) showToast("Game saved to history");
    return succeeded;
  };
  const forfeitGame = async (
    forfeitWinner: "us" | "them",
  ): Promise<boolean> => {
    if (!game || blockedByScorer()) return false;
    let revision = game.revision;
    const forfeitActionId = newActionId();
    const archiveActionId = newActionId();
    if (game.status !== "final") {
      const response = await enqueue(
        async () => {
          let committedRevision: number | null = null;
          const succeeded = await run(
            () =>
              intents.sendForfeit({
                teamId,
                gameId: game.id,
                expectedRevision: revision,
                forfeitWinner,
                actionId: forfeitActionId,
              }),
            (value) => {
              committedRevision = responseRevision(value);
            },
          );
          return succeeded ? committedRevision : null;
        },
        {
          key: actionKeys.forfeitRecord(game.id),
          chainKey: `game:${game.id}`,
          intentId: forfeitActionId,
          recovery: {
            teamId,
            actionId: forfeitActionId,
            resolve: (revision: number | null) => revision ?? undefined,
          },
        },
      );
      if (response === false || response === null) return false;
      revision = response;
    }
    const succeeded = await enqueue(
      () =>
        run(() =>
          intents.sendArchive({
            teamId,
            gameId: game.id,
            expectedRevision: revision,
            actionId: archiveActionId,
          }),
        ),
      {
        key: actionKeys.forfeitSave(game.id),
        chainKey: `game:${game.id}`,
        intentId: archiveActionId,
        recovery: eventRecovery(teamId, archiveActionId),
      },
    );
    if (succeeded) {
      showToast(
        forfeitWinner === "us"
          ? "Forfeit recorded — win saved to history"
          : "Forfeit recorded — loss saved to history",
      );
    }
    return succeeded;
  };
  const discardActiveGame = (): Promise<boolean> => {
    if (!game || blockedByScorer()) return Promise.resolve(false);
    const actionId = newActionId();
    return enqueue(
      () =>
        run(() =>
          intents.sendDiscard({
            teamId,
            gameId: game.id,
            expectedRevision: game.revision,
            actionId,
          }),
        ),
      {
        key: actionKeys.discardGame,
        intentId: actionId,
        recovery: eventRecovery(teamId, actionId),
      },
    );
  };
  const claimScoring = (): Promise<boolean> => {
    if (!game) return Promise.resolve(false);
    const actionId = newActionId();
    return enqueue(
      () =>
        run(
          () => intents.sendClaimScoring({ teamId, gameId: game.id, actionId }),
          (response) => {
            onScorerClaimed(response.data.scorerNameSnapshot ?? null);
            showToast("You are now scoring");
          },
        ),
      {
        key: actionKeys.claimScoring,
        intentId: actionId,
        recovery: eventRecovery(teamId, actionId),
      },
    );
  };

  return {
    gameAction,
    recordPlay,
    applyRBI,
    advanceRunner,
    removeRunner,
    addOppRun,
    endHalf,
    undo,
    toggleOpenInning,
    keepPlaying,
    endAndArchive,
    forfeitGame,
    discardActiveGame,
    claimScoring,
  };
}
